import React, { ReactElement, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useMount } from 'react-use';
import { Column, Field, ISelectOption, UIType } from '@zeroedin-tech/zi-common-ui/lib/';
import { useToast } from '@zeroedin-tech/zi-common-ui/lib/components/toast/ToastProvider';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPenToSquare } from '@fortawesome/pro-regular-svg-icons';
import { Alert, AlertVariant, Button, Modal, ModalVariant } from '@patternfly/react-core';
import SchnurForm from '@zeroedin-tech/zi-common-ui/lib/components/SchnurForm/SchnurForm';
import { DatabaseServer, TDatabaseServer } from '../api/databaseServer/DatabaseServer';
import { SubheaderContext } from '../layout/Layout';
import PageTitleSubheader from '../layout/subheader/PageTitleSubheader';
import FilterTableLayout from '../layout/FilterTableLayout';
import ZiTable, { Action } from '@zeroedin-react/zi-common/lib/components/table/ZiTable';
import { DummyPermission, useIsGranted } from '../helper/PermissionOverride';

export default function DatabaseServers(): ReactElement {
	const { addToast } = useToast();
	const [setSubheader]: SubheaderContext = useOutletContext();
	const [tableData, setTableData] = useState<TDatabaseServer[]>([]);
	const [tableLoading, setTableLoading] = useState<boolean>(true);
	const [loadError, setLoadError] = useState<string | null>(null);
	const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
	const [activeDatabaseServer, setActiveDatabaseServer] = useState<TDatabaseServer>(
		DatabaseServer.Default() as TDatabaseServer
	);
	const [isFormLoading, setIsFormLoading] = useState<boolean>(false);

	const selectedColumns: Column<TDatabaseServer>[] = [
		{
			title: 'Name',
			columnName: 'name',
			sortable: true,
		},
		{
			title: 'Host',
			columnName: 'host',
			sortable: true,
		},
		{
			title: 'Driver',
			columnName: 'driver',
			sortable: true,
		},
		{
			title: 'Port',
			columnName: 'port',
			sortable: true,
		},
		{
			title: 'Username',
			columnName: 'db_user',
			sortable: true,
		},
	];

	const actions: Action<TDatabaseServer, typeof DummyPermission>[] = [
		{
			name: (
				<>
					Edit <FontAwesomeIcon icon={faPenToSquare} />
				</>
			),
			callback: (item) => {
				setActiveDatabaseServer(item);
				setIsModalOpen(true);
			},
			permission: DummyPermission.EDIT,
		},
		{
			name: 'Delete',
			callback: (item) => {
				setTableLoading(true);

				DatabaseServer.Delete(item.id)
					.then(() => {
						setTableLoading(false);
						setTableData((prev) => prev.filter((user) => user.id !== item.id));
						addToast('Database Server deleted successfully.', AlertVariant.success);
					})
					.catch(() => {
						setTableLoading(false);
						addToast('Failed to delete Database Server.', AlertVariant.danger);
					});
			},
		},
	];

	const formProperties: Field<TDatabaseServer>[] = [
		{
			title: 'Nickname',
			columnName: 'name',
			uiSchema: {
				type: UIType.TEXT,
				helpText: 'An alias for the database server to help you identify it quickly.',
			},
			required: true,
		},
		{
			title: 'Driver',
			columnName: 'driver',
			uiSchema: {
				type: UIType.SELECT,
				options: ['PostgreSQL'].map((driver) => ({
					key: driver,
					value: driver,
				})),
				initialSelection:
					'id' in activeDatabaseServer ? activeDatabaseServer.driver : 'PostgreSQL',
				onSelect: (value: ISelectOption) => {
					return value.key;
				},
			},
			required: true,
		},
		{
			title: 'Host Name',
			columnName: 'host',
			uiSchema: {
				type: UIType.TEXT,
				placeholder: 'database.example.com',
				helpText: 'The hostname or IP address of the database server.',
			},
			required: true,
		},
		{
			title: 'Username',
			columnName: 'db_user',
			uiSchema: {
				type: UIType.TEXT,
			},
			required: true,
		},
		{
			title: 'Password',
			columnName: 'password',
			uiSchema: {
				type: UIType.PASSWORD,
			},
			required: true,
		},
		{
			title: 'Charset',
			columnName: 'charset',
			uiSchema: {
				type: UIType.TEXT,
			},
			required: true,
		},
		{
			title: 'Port',
			columnName: 'port',
			uiSchema: {
				type: UIType.NUMBER,
			},
			required: true,
		},
	];

	useMount(() => {
		setSubheader(
			<PageTitleSubheader
				pageTitle="Database Servers"
				pageDescription="Manage your database servers."
			/>
		);

		DatabaseServer.GetAll()
			.then((tableData) => {
				setTableData(tableData);
				setLoadError(null);
			})
			.catch(() => {
				const message = 'Failed to load Database Servers.';
				setLoadError(message);
				addToast(message, AlertVariant.danger);
			})
			.finally(() => {
				setTableLoading(false);
			});
	});

	const addButton = (
		<Button
			data-testid={'systemUser-create'}
			variant={'primary'}
			onClick={() => {
				setActiveDatabaseServer(DatabaseServer.Default() as TDatabaseServer);
				setIsModalOpen(true);
			}}
		>
			New Database Server
		</Button>
	);

	const handleClose = () => {
		setIsModalOpen(false);
	};

	const handleSuccess = (value: TDatabaseServer) => {
		const index = tableData.findIndex((row) => row.id === value.id);

		if (index >= 0) {
			setTableData((prev) => [...prev.slice(0, index), value, ...prev.slice(index + 1)]);
		} else {
			setTableData((prev) => [...prev, value]);
		}

		setIsModalOpen(false);
	};

	const entityTable = (
		<ZiTable<TDatabaseServer, typeof DummyPermission>
			ariaLabel={'Database Servers'}
			columns={selectedColumns}
			data={tableData}
			caption="Database Servers"
			actions={actions}
			loading={tableLoading}
			useIsGranted={useIsGranted}
			Permission={DummyPermission}
		/>
	);

	return (
		<React.Fragment>
			<Modal
				variant={ModalVariant.medium}
				title="Database Server Management"
				isOpen={isModalOpen}
				onClose={handleClose}
			>
				<SchnurForm<TDatabaseServer>
					title={'Database Server Management'}
					fields={formProperties}
					initialSubject={activeDatabaseServer}
					isLoading={isFormLoading}
					onSubmit={(entity) => {
						setIsFormLoading(true);

						if (entity.id) {
							DatabaseServer.Update(entity)
								.then((updated) => {
									handleSuccess(updated);
								})
								.catch(() => {
									addToast(
										'An error occurred while trying to save the Database Server. Please try again later.',
										AlertVariant.danger
									);
								})
								.finally(() => {
									setIsFormLoading(false);
								});
						} else {
							DatabaseServer.New(entity)
								.then((newDim) => {
									handleSuccess(newDim);
								})
								.catch(() => {
									addToast(
										'An error occurred while trying to save the Database Server. Please try again later.',
										AlertVariant.danger
									);
								})
								.finally(() => {
									setIsFormLoading(false);
								});
						}
					}}
				/>
			</Modal>
			<React.Fragment>
				{loadError && (
					<Alert
						variant="danger"
						isInline
						title={loadError}
						data-testid={'databaseServer-load-error'}
					/>
				)}
				<FilterTableLayout
					table={entityTable}
					layoutActions={addButton}
				/>
			</React.Fragment>
		</React.Fragment>
	);
}
